import { useEffect, useMemo, useRef, useState } from 'react';
import { teamColor, teamSortIndex } from './teams';

export const DriverTeamFilter = ({
  availableDrivers,
  selectedDrivers,
  onChange,
  drivers = [],
}) => {
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const handleDoc = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleDoc);
    return () => document.removeEventListener('mousedown', handleDoc);
  }, [open]);

  const selectedSet = new Set(selectedDrivers);
  const availableSet = new Set(availableDrivers);

  // driverInfo: { code -> {team, teamColorHex} } from the per-session API.
  const driverInfo = useMemo(() => {
    const m = new Map();
    for (const d of drivers) m.set(d.driver, d);
    return m;
  }, [drivers]);

  // Group available drivers by team and sort by Splitlap's display order.
  const teamsOnGrid = useMemo(() => {
    const byTeam = new Map();
    for (const code of availableDrivers) {
      const info = driverInfo.get(code);
      const team = info?.team || 'Unknown';
      if (!byTeam.has(team)) byTeam.set(team, []);
      byTeam.get(team).push(code);
    }
    return Array.from(byTeam.entries())
      .map(([team, codes]) => ({
        team,
        drivers: codes.sort(),
        colorHex: driverInfo.get(codes[0])?.teamColorHex || null,
      }))
      .sort((a, b) => teamSortIndex(a.team) - teamSortIndex(b.team));
  }, [availableDrivers, driverInfo]);

  const toggleDriver = (code) => {
    const next = new Set(selectedSet);
    if (next.has(code)) next.delete(code);
    else next.add(code);
    onChange(Array.from(next));
  };

  const toggleTeam = (team, teamDrivers) => {
    const usable = teamDrivers.filter((d) => availableSet.has(d));
    if (usable.length === 0) return;
    const allSelected = usable.every((d) => selectedSet.has(d));
    const next = new Set(selectedSet);
    if (allSelected) usable.forEach((d) => next.delete(d));
    else usable.forEach((d) => next.add(d));
    onChange(Array.from(next));
  };

  const summary =
    selectedDrivers.length === 0
      ? 'All drivers'
      : `${selectedDrivers.length} driver${selectedDrivers.length === 1 ? '' : 's'}`;

  return (
    <div ref={rootRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="text-xs flex items-center gap-1.5 rounded-md border border-f1-border bg-f1-surface px-2.5 py-1 hover:border-f1-muted text-f1-text"
      >
        <span>{summary}</span>
        <span className="text-f1-muted">▾</span>
      </button>

      {open && (
        <div className="absolute right-0 mt-1.5 z-20 w-[280px] rounded-lg border border-f1-border bg-f1-surface p-3 shadow-xl">
          <div className="flex items-center justify-between mb-2">
            <span className="text-[10px] uppercase tracking-wider text-f1-muted">
              Filter
            </span>
            {selectedDrivers.length > 0 && (
              <button
                onClick={() => onChange([])}
                className="text-[10px] uppercase tracking-wider text-f1-muted hover:text-f1-text"
              >
                Clear
              </button>
            )}
          </div>

          <div className="flex flex-wrap gap-1 mb-3">
            {teamsOnGrid.map(({ team, drivers: teamDrivers, colorHex }) => {
              const usable = teamDrivers.filter((d) => availableSet.has(d));
              const allSelected =
                usable.length > 0 && usable.every((d) => selectedSet.has(d));
              const color = teamColor(team, colorHex);
              return (
                <button
                  key={team}
                  onClick={() => toggleTeam(team, teamDrivers)}
                  className="text-[10px] px-2 py-0.5 rounded-full border transition-colors"
                  style={{
                    borderColor: color,
                    backgroundColor: allSelected ? color : 'transparent',
                    color: allSelected ? '#0a0a0a' : color,
                  }}
                >
                  {team}
                </button>
              );
            })}
          </div>

          <div className="flex flex-wrap gap-1">
            {availableDrivers.map((code) => {
              const info = driverInfo.get(code);
              const color = info ? teamColor(info.team, info.teamColorHex) : '#9ca3af';
              const selected = selectedSet.has(code);
              return (
                <button
                  key={code}
                  onClick={() => toggleDriver(code)}
                  className="text-[10px] font-mono px-1.5 py-0.5 rounded border transition-colors"
                  style={{
                    borderColor: color,
                    backgroundColor: selected ? color : 'transparent',
                    color: selected ? '#0a0a0a' : '#e5e5e5',
                  }}
                >
                  {code}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};
